/* ============================================================
   OSD — channel badge, now-playing banner, volume bar.
   Each overlay's visibility is a store flag; the content is
   derived from current channel + programIndex/queue + volume/muted.
   ============================================================ */
import { useTv } from '@/tv/context'
import { queueIndexToProgramOrdinal, programCount } from '@/tv/breakResolver'
import styles from './Osd.module.css'

const pad = (n: number) => (n < 10 ? '0' : '') + n
const SEG_COUNT = 15

export function Osd() {
  const { state, current } = useTv()
  const ch = current
  const { osd, osdOverride, offAirNumber, programIndex, volume, muted } = state
  const offAir = offAirNumber != null
  // Now-playing is derived straight from the queue + the current program index —
  // the same source the guide uses. No separate liveProgram snapshot.
  const liveOrdinal = state.queue.length ? queueIndexToProgramOrdinal(state.queue, programIndex) : programIndex
  const liveTotal = state.queue.length ? programCount(state.queue) : undefined

  // --- channel badge --- (dial override > off-air number > current channel)
  // HIDEO is the secret channel: the badge renders the WORD, not a CH number.
  const isHideo = !osdOverride && !offAir && ch.slug === 'hideo'
  const chNum = osdOverride ? osdOverride.num : offAir ? offAirNumber : ch.number
  const chLabel = osdOverride
    ? osdOverride.label
    : offAir
      ? 'NO SIGNAL'
      : ch.genre || ''

  // --- volume segments ---
  const lit = muted ? 0 : Math.round((volume / 100) * SEG_COUNT)

  return (
    <>
      <div
        className={
          [
            styles.osd,
            styles.channel,
            osd.channel && styles.show,
            osdOverride?.confirm && styles.confirm,
          ]
            .filter(Boolean)
            .join(' ')
        }
      >
        <div className={styles.cnum}>{isHideo ? 'HIDEO' : `CH ${pad(chNum)}`}</div>
        <div className={styles.clabel}>{isHideo ? '' : chLabel || '—'}</div>
      </div>

      <div className={[styles.osd, styles.banner, osd.banner && styles.show].filter(Boolean).join(' ')}>
        {liveTotal ? <span className={styles.prog}>PROG {pad(liveOrdinal + 1)}/{pad(liveTotal)}</span> : null}
      </div>

      <div className={[styles.osd, styles.live, state.live && styles.show].filter(Boolean).join(' ')}>
        <span className={styles.liveDot} />LIVE
      </div>

      <div
        className={[styles.osd, styles.volume, (osd.volume || muted) && styles.show, muted && styles.muted]
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.vlabel}>{muted ? 'MUTED' : 'VOL'}</span>
        <span className={styles.vbar}>
          {Array.from({ length: SEG_COUNT }, (_, i) => (
            <span key={i} className={[styles.seg, i < lit && styles.on].filter(Boolean).join(' ')} />
          ))}
        </span>
      </div>
    </>
  )
}
